import { useRef, type CSSProperties, type ReactNode } from "react";
import { usePrefersReducedMotion } from "../hooks/usePrefersReducedMotion";

type MagneticButtonProps = {
  children: ReactNode;
  /** Maksymalne przesunięcie w px */
  strength?: number;
  className?: string;
  style?: CSSProperties;
  onClick?: () => void;
};

/**
 * Owija istniejący przycisk/CTA. Na urządzeniach z myszką (pointer: fine)
 * przycisk delikatnie "przyciąga się" do kursora w promieniu swojego obszaru,
 * a po opuszczeniu wraca sprężyście do pozycji zerowej.
 *
 * Na dotyku (telefon/tablet) i przy prefers-reduced-motion efekt jest
 * całkowicie wyłączony - przycisk zachowuje się jak zwykły element.
 *
 * Użycie:
 *   <MagneticButton>
 *     <a className="bg-[#BEF264] px-6 py-3 text-black">Rozpocznij sprint rekrutacyjny</a>
 *   </MagneticButton>
 */
export function MagneticButton({ children, strength = 12, className, style, onClick }: MagneticButtonProps) {
  const ref = useRef<HTMLDivElement>(null);
  const reducedMotion = usePrefersReducedMotion();

  const isFinePointer =
    typeof window !== "undefined" && window.matchMedia && window.matchMedia("(pointer: fine)").matches;

  const enabled = !reducedMotion && isFinePointer;

  function handleMouseMove(e: React.MouseEvent<HTMLDivElement>) {
    if (!enabled) return;
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const relX = e.clientX - rect.left - rect.width / 2;
    const relY = e.clientY - rect.top - rect.height / 2;
    const x = (relX / (rect.width / 2)) * strength;
    const y = (relY / (rect.height / 2)) * strength;
    el.style.transform = `translate(${x}px, ${y}px)`;
    el.style.transition = "transform 60ms linear";
  }

  function handleMouseLeave() {
    const el = ref.current;
    if (!el) return;
    el.style.transform = "translate(0, 0)";
    el.style.transition = "transform 400ms cubic-bezier(0.34, 1.56, 0.64, 1)"; // lekki spring-back
  }

  return (
    <div
      ref={ref}
      className={className}
      style={{ display: "inline-block", ...style }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      onClick={onClick}
    >
      {children}
    </div>
  );
}
